import React, { useState, useMemo, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Medal, Crown, Star, Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { key: 'All', label: 'All Participants', color: '#08513F' },
  { key: 'Muslim', label: 'Muslim Boys', color: '#2A9D8F' },
  { key: 'Muslimah', label: 'Muslimah Girls', color: '#E76F51' }
];

// Memoized Podium Participant Component
const PodiumParticipant = memo(({ child, actualRank }) => {
  const profileImage = child.profile_image;
  const isLoading = false;

  // Determine podium styling based on actual rank value, not position
  const getPodiumStyling = (rank) => {
    switch (rank) {
      case 1:
        return {
          height: 'h-20 sm:h-24 lg:h-32',
          avatarSize: 'h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20',
          fontSize: 'text-base sm:text-lg lg:text-xl',
          pointsSize: 'text-lg sm:text-xl lg:text-2xl',
          podiumColor: 'bg-gradient-to-t from-[#D4AF37] to-[#F4D03F]',
          glowClass: 'animate-podium-glow-gold',
          borderColor: 'border-[#D4AF37]'
        };
      case 2:
        return {
          height: 'h-16 sm:h-20 lg:h-28',
          avatarSize: 'h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16',
          fontSize: 'text-sm sm:text-base lg:text-lg',
          pointsSize: 'text-base sm:text-lg lg:text-xl',
          podiumColor: 'bg-gradient-to-t from-[#C0C0C0] to-[#E8E8E8]',
          glowClass: 'animate-podium-glow-silver',
          borderColor: 'border-[#C0C0C0]'
        };
      case 3:
        return {
          height: 'h-14 sm:h-16 lg:h-24',
          avatarSize: 'h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16',
          fontSize: 'text-sm sm:text-base lg:text-lg',
          pointsSize: 'text-base sm:text-lg lg:text-xl',
          podiumColor: 'bg-gradient-to-t from-[#CD7F32] to-[#E8A84C]',
          glowClass: 'animate-podium-glow-bronze',
          borderColor: 'border-[#CD7F32]'
        };
      default:
        // For ranks beyond 3, use bronze styling
        return {
          height: 'h-14 sm:h-16 lg:h-24',
          avatarSize: 'h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16',
          fontSize: 'text-sm sm:text-base lg:text-lg',
          pointsSize: 'text-base sm:text-lg lg:text-xl',
          podiumColor: 'bg-gradient-to-t from-[#CD7F32] to-[#E8A84C]',
          glowClass: 'animate-podium-glow-bronze',
          borderColor: 'border-[#CD7F32]'
        };
    }
  };

  const styling = getPodiumStyling(actualRank);

  return (
    <div
      className="flex flex-col items-center flex-shrink-0 animate-slide-up"
    >
      {/* Crown for 1st place */}
      {actualRank === 1 && (
        <Crown className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-[#D4AF37] mb-1 animate-bounce" />
      )}

      {/* Avatar */}
      <Avatar
        className={`${styling.avatarSize} shadow-lg mb-1 sm:mb-2 border-2 ${styling.borderColor} ${styling.glowClass}`}
      >
        <AvatarImage src={profileImage} loading="eager" />
        <AvatarFallback className={`${styling.fontSize} font-bold ${
          child.gender === 'Muslim'
            ? 'bg-[#08513F] text-white'
            : 'bg-[#E76F51] text-white'
        }`}>
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            `${child.first_name?.charAt(0)}${child.last_name?.charAt(0)}`
          )}
        </AvatarFallback>
      </Avatar>

      {/* Name */}
      <p className={`text-white font-bold font-outfit ${styling.fontSize} text-center mb-1 px-1 truncate max-w-24`}>
        {child.first_name} {child.last_name?.charAt(0)}.
      </p>

      {/* Points */}
      <div className="flex items-center gap-1 text-[#D4AF37] mb-2 sm:mb-3">
        <Star className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
        <span className={`font-bold font-outfit ${styling.pointsSize}`}>
          {child.total_points}
        </span>
      </div>

      {/* Podium */}
      <div className={`w-14 sm:w-16 lg:w-20 ${styling.height} ${styling.podiumColor} rounded-t-lg flex items-center justify-center shadow-lg relative overflow-hidden ${
        styling.glowClass
      }`}>
        <span className="text-base sm:text-lg lg:text-xl font-black text-white/90 font-outfit relative z-10">
          {actualRank}
        </span>
      </div>
    </div>
  );
});

PodiumParticipant.displayName = 'PodiumParticipant';

// Memoized Leaderboard Item Component
const LeaderboardItem = memo(({ child }) => {
  const profileImage = child.profile_image;
  const isLoading = false;
  
  return (
    <Card 
      className="bg-white rounded-2xl border-[#D4AF37]/20 shadow-md hover:shadow-lg transition-shadow animate-slide-up"
      style={{ 
        animation: 'slideUp 0.3s ease-out',
        animationFillMode: 'both'
      }}
      data-testid={`rank-${child.rank}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="w-12 h-12 rounded-full bg-[#FFFBEB] flex items-center justify-center">
            <span className="text-xl font-bold text-[#264653] font-outfit">
              {child.dense_rank}
            </span>
          </div>
          
          {/* Avatar */}
          <Avatar className="h-14 w-14 border-2 border-[#D4AF37]/30">
            <AvatarImage src={profileImage} />
            <AvatarFallback className={`font-bold ${
              child.gender === 'Muslim' 
                ? 'bg-[#08513F] text-white' 
                : 'bg-[#E76F51] text-white'
            }`}>
              {isLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                `${child.first_name?.charAt(0)}${child.last_name?.charAt(0)}`
              )}
            </AvatarFallback>
          </Avatar>
          
          {/* Info */}
          <div className="flex-1">
            <h3 className="font-bold text-[#264653] font-outfit">
              {child.first_name} {child.last_name}
            </h3>
            <p className="text-sm text-[#6B7280]">{child.gender}</p>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="flex items-center gap-1 text-[#D4AF37]">
                <Star className="w-4 h-4" />
                <span className="font-bold text-lg">{child.total_points}</span>
              </div>
              <p className="text-xs text-[#6B7280]">Points</p>
            </div>
            <div className="text-center">
              <div className="flex items-center gap-1 text-[#E76F51]">
                <Flame className="w-4 h-4" />
                <span className="font-bold text-lg">{child.current_streak}</span>
              </div>
              <p className="text-xs text-[#6B7280]">Streak</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

LeaderboardItem.displayName = 'LeaderboardItem';

export default function Leaderboard() {
  const [filter, setFilter] = useState('All');

  // Fetch dense rank leaderboard data with TanStack Query
  const { data: allLeaderboard = [], isLoading: loading, error } = useQuery({
    queryKey: ['dense-rank-leaderboard', filter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter !== 'All') {
        params.append('gender', filter);
      }
      const response = await fetch(`${API_URL}/api/leaderboard/dense-rank?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch dense rank leaderboard');
      }
      return response.json();
    },
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
    staleTime: 10000,
  });

  const leaderboard = allLeaderboard;

  // Calculate leaderboard slices - now showing top 20
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3).filter(child => child.dense_rank <= 20);

  // Reorder for podium: [2nd, 1st, 3rd]
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="space-y-4 w-full max-w-xs sm:max-w-md">
            {/* Loading skeleton for podium */}
            <div className="flex items-end justify-center gap-2 sm:gap-4 lg:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="skeleton w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full mb-2 sm:mb-4" />
                  <div className="skeleton w-16 h-20 sm:w-20 sm:h-24 lg:w-24 lg:h-32 rounded-t-2xl" />
                </div>
              ))}
            </div>
            {/* Loading skeleton for list */}
            <div className="space-y-2 mt-4 sm:mt-6 lg:mt-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton h-10 sm:h-12 lg:h-16 rounded-2xl" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout>
        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#264653] font-outfit">Leaderboard</h1>
              <p className="text-[#6B7280] mt-1">See who's leading the challenge</p>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2">
              {['All', 'Muslim', 'Muslimah'].map((option) => (
                <Button
                  key={option}
                  onClick={() => setFilter(option)}
                  variant={filter === option ? 'default' : 'outline'}
                  className={`rounded-full px-6 ${
                    filter === option 
                      ? 'bg-[#08513F] text-white' 
                      : 'border-2 border-[#08513F]/20 text-[#264653]'
                  }`}
                  data-testid={`filter-${option.toLowerCase()}`}
                >
                  {option}
                </Button>
              ))}
            </div>
          </div>

          {/* No Data */}
          {leaderboard.length === 0 ? (
            <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
              <Medal className="w-16 h-16 text-[#D4AF37]/50 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Participants Yet</h2>
              <p className="text-[#6B7280]">Be the first to join the challenge and climb the leaderboard!</p>
            </Card>
          ) : error ? (
            <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
              <Medal className="w-16 h-16 text-[#D4AF37]/50 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">Failed to load leaderboard</h2>
              <p className="text-[#6B7280]">Please try refreshing the page</p>
            </Card>
          ) : (
            <>
              {/* Podium */}
              {top3.length > 0 && (
                <Card className="bg-islamic-green rounded-3xl border-0 shadow-xl overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <Crown className="w-8 h-8 text-[#D4AF37]" />
                      <h2 className="text-2xl font-bold text-white font-outfit">Top Champions</h2>
                    </div>

                    <div className="flex items-end justify-center gap-2 sm:gap-3 lg:gap-6">
                      {podiumOrder.map((child, i) => {
                        if (!child) return null;
                        
                        const actualRank = child.dense_rank;
                        
                        return (
                          <PodiumParticipant
                            key={child.child_id}
                            child={child}
                            actualRank={actualRank}
                          />
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Rest of Leaderboard (4-20) - Now showing top 20 */}
              {rest.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-[#264653] font-outfit mb-4">Rankings {rest.length > 0 && `(4-${Math.min(20, leaderboard.length)})`}</h2>
                  
                  <div className="space-y-3">
                    {rest.map((child) => {
                      return (
                        <LeaderboardItem
                          key={child.child_id}
                          child={child}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Layout>

      {/* Inline Styles for Animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Loading skeleton */
        .skeleton {
          background: linear-gradient(90deg, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 75%);
          background-size: 200% 100%;
          animation: pulse 1.5s infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        /* Custom podium animations */
        @keyframes glow-gold {
          0%, 100% {
            box-shadow: 0 0 20px rgba(212, 175, 55, 0.8);
          }
          50% {
            box-shadow: 0 0 30px rgba(212, 175, 55, 0.4);
          }
        }
        
        @keyframes glow-silver {
          0%, 100% {
            box-shadow: 0 0 20px rgba(192, 192, 192, 0.8);
          }
          50% {
            box-shadow: 0 0 30px rgba(192, 192, 192, 0.4);
          }
        }
        
        @keyframes glow-bronze {
          0%, 100% {
            box-shadow: 0 0 20px rgba(205, 127, 50, 0.8);
          }
          50% {
            box-shadow: 0 0 30px rgba(205, 127, 50, 0.4);
          }
        }
        
        .podium-glow-gold {
          animation: glow-gold 2s ease-in-out infinite;
        }
        
        .podium-glow-silver {
          animation: glow-silver 2s ease-in-out infinite;
        }
        
        .podium-glow-bronze {
          animation: glow-bronze 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
